import React from "react";
import "./SupportResistanceVisual.css";

function SupportResistanceVisual() {
  return (
    <div className="lessonVisualCard supportResistanceVisual">
      <div className="lessonVisualHeader">
        <div>
          <span>PRICE LEVEL VISUAL</span>
          <h3>Support, resistance, breakouts, and retests</h3>
        </div>

        <strong className="supportResistanceBadge">
          Price structure
        </strong>
      </div>

      <div className="supportResistanceBody">
        <div className="supportResistanceChart">
          <span className="visualPanelLabel">
            REPEATED PRICE REACTIONS
          </span>

          <svg
            viewBox="0 0 900 430"
            role="img"
            aria-label="Price reacting between support and resistance before breaking out"
          >
            <line
              x1="70"
              y1="115"
              x2="830"
              y2="115"
              className="resistanceLevelLine"
            />

            <line
              x1="70"
              y1="315"
              x2="830"
              y2="315"
              className="supportLevelLine"
            />

            <polyline
              points="
                80,285
                150,235
                220,135
                285,205
                350,300
                420,245
                490,125
                555,195
                620,305
                685,230
                735,105
                785,70
                830,48
              "
              className="supportResistancePriceLine"
            />

            <circle
              cx="220"
              cy="135"
              r="9"
              className="resistanceReactionPoint"
            />
            <circle
              cx="490"
              cy="125"
              r="9"
              className="resistanceReactionPoint"
            />
            <circle
              cx="350"
              cy="300"
              r="9"
              className="supportReactionPoint"
            />
            <circle
              cx="620"
              cy="305"
              r="9"
              className="supportReactionPoint"
            />
            <circle
              cx="735"
              cy="105"
              r="10"
              className="breakoutPoint"
            />

            <text
              x="82"
              y="100"
              className="resistanceLevelText"
            >
              Resistance zone $10.00
            </text>

            <text
              x="82"
              y="342"
              className="supportLevelText"
            >
              Support zone $9.40
            </text>

            <text
              x="185"
              y="165"
              className="reactionText"
            >
              Rejection
            </text>

            <text
              x="445"
              y="155"
              className="reactionText"
            >
              Rejection
            </text>

            <text
              x="322"
              y="285"
              className="supportReactionText"
            >
              Buyers respond
            </text>

            <text
              x="585"
              y="286"
              className="supportReactionText"
            >
              Buyers respond
            </text>

            <text
              x="705"
              y="92"
              className="breakoutLabel"
            >
              Breakout
            </text>

            <text
              x="738"
              y="38"
              className="followThroughText"
            >
              Follow-through
            </text>
          </svg>
        </div>

        <div className="supportResistanceMeaning">
          <span>HOW TO READ THE LEVELS</span>

          <div className="supportResistanceMeaningItem">
            <strong>1</strong>

            <div>
              <small>SUPPORT</small>
              <h4>Buyers repeatedly defend an area</h4>
              <p>
                Price slows or reverses when buying demand appears
                near the same zone.
              </p>
            </div>
          </div>

          <div className="supportResistanceMeaningItem">
            <strong>2</strong>

            <div>
              <small>RESISTANCE</small>
              <h4>Sellers repeatedly defend an area</h4>
              <p>
                Price struggles to continue higher when selling
                pressure appears near the same zone.
              </p>
            </div>
          </div>

          <div className="supportResistanceMeaningItem">
            <strong>3</strong>

            <div>
              <small>BREAKOUT</small>
              <h4>Price moves through resistance</h4>
              <p>
                Volume, a strong close, and continued holding above
                the level provide better confirmation.
              </p>
            </div>
          </div>

          <p className="supportResistanceZoneNote">
            Support and resistance are usually zones, not exact
            one-cent prices.
          </p>
        </div>
      </div>

      <div className="supportResistanceRetestGrid">
        <div className="supportResistanceRetestChart">
          <span className="visualPanelLabel">
            RESISTANCE BECOMES SUPPORT
          </span>

          <svg
            viewBox="0 0 900 340"
            role="img"
            aria-label="Price breaking above resistance and retesting the level as support"
          >
            <line
              x1="70"
              y1="205"
              x2="830"
              y2="205"
              className="retestLevelLine"
            />

            <polyline
              points="
                80,285
                155,245
                225,215
                295,235
                365,190
                435,135
                505,90
                575,145
                645,200
                710,165
                775,100
                830,60
              "
              className="retestPriceLine"
            />

            <circle
              cx="645"
              cy="200"
              r="10"
              className="retestHoldPoint"
            />

            <text
              x="82"
              y="192"
              className="retestLevelText"
            >
              Previous resistance $10.00
            </text>

            <text
              x="470"
              y="80"
              className="retestBreakoutText"
            >
              Breakout
            </text>

            <text
              x="600"
              y="232"
              className="retestHoldText"
            >
              Retest holds
            </text>

            <text
              x="690"
              y="88"
              className="retestContinueText"
            >
              Buyers regain control
            </text>
          </svg>
        </div>

        <div className="supportResistanceChecklist">
          <span>CONFIRMATION CHECKLIST</span>

          <div>
            <strong>Volume</strong>
            <p>
              Trading activity increases as price moves through
              resistance.
            </p>
          </div>

          <div>
            <strong>Close</strong>
            <p>
              The candle closes above the level instead of quickly
              falling back below it.
            </p>
          </div>

          <div>
            <strong>Retest</strong>
            <p>
              Buyers defend the former resistance area when price
              pulls back.
            </p>
          </div>

          <div>
            <strong>Reward versus risk</strong>
            <p>
              The next resistance level leaves enough room for a
              reasonable target.
            </p>
          </div>
        </div>
      </div>

      <section className="supportResistanceSetupSection">
        <div className="supportResistanceSetupHeader">
          <div>
            <span>HOW TO SET IT UP</span>
            <h3>How to draw support and resistance on a chart</h3>
          </div>

          <strong>Step by step</strong>
        </div>

        <div className="supportResistanceSetupGrid">
          <div className="supportResistanceSetupChart">
            <span className="visualPanelLabel">
              DRAW THE ZONES
            </span>

            <svg
              viewBox="0 0 920 420"
              role="img"
              aria-label="Example showing how to draw support and resistance zones from repeated price reactions"
            >
              <rect
                x="140"
                y="92"
                width="620"
                height="34"
                rx="8"
                className="resistanceZoneArea"
              />

              <rect
                x="140"
                y="290"
                width="620"
                height="34"
                rx="8"
                className="supportZoneArea"
              />

              <polyline
                points="
                  90,270
                  150,225
                  210,160
                  270,110
                  330,185
                  390,280
                  455,225
                  520,120
                  585,170
                  645,295
                  710,225
                  770,105
                  830,155
                "
                className="setupPriceLine"
              />

              <circle
                cx="270"
                cy="110"
                r="9"
                className="setupResistancePoint"
              />
              <circle
                cx="520"
                cy="120"
                r="9"
                className="setupResistancePoint"
              />
              <circle
                cx="390"
                cy="280"
                r="9"
                className="setupSupportPoint"
              />
              <circle
                cx="645"
                cy="295"
                r="9"
                className="setupSupportPoint"
              />

              <text
                x="145"
                y="82"
                className="setupResistanceText"
              >
                Resistance zone
              </text>

              <text
                x="145"
                y="350"
                className="setupSupportText"
              >
                Support zone
              </text>

              <text
                x="228"
                y="145"
                className="setupReactionLabel"
              >
                Price rejected here
              </text>

              <text
                x="480"
                y="155"
                className="setupReactionLabel"
              >
                Price rejected here
              </text>

              <text
                x="330"
                y="255"
                className="setupBuyerLabel"
              >
                Buyers defended here
              </text>

              <text
                x="610"
                y="270"
                className="setupBuyerLabel"
              >
                Buyers defended here
              </text>
            </svg>
          </div>

          <div className="supportResistanceSetupSteps">
            <span>DRAWING RULES</span>

            <div className="supportResistanceSetupStep">
              <strong>1</strong>
              <div>
                <small>LOOK LEFT</small>
                <h4>Find repeated reactions</h4>
                <p>
                  Look for areas where price repeatedly stops,
                  reverses, or struggles to continue.
                </p>
              </div>
            </div>

            <div className="supportResistanceSetupStep">
              <strong>2</strong>
              <div>
                <small>DRAW A ZONE</small>
                <h4>Mark an area, not one tiny line</h4>
                <p>
                  Use the group of highs or lows to draw a price zone
                  where reactions happen more than once.
                </p>
              </div>
            </div>

            <div className="supportResistanceSetupStep">
              <strong>3</strong>
              <div>
                <small>WAIT FOR PRICE</small>
                <h4>Let price come back to the level</h4>
                <p>
                  Support and resistance matter most when price
                  returns to the zone and reacts again.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="supportResistanceSetupRules">
          <div>
            <span>1</span>
            <div>
              <strong>More touches = stronger level</strong>
              <p>
                A level becomes easier to trust when price reacts to
                it several times.
              </p>
            </div>
          </div>

          <div>
            <span>2</span>
            <div>
              <strong>Use candle highs and lows</strong>
              <p>
                Resistance often forms near repeated highs and support
                often forms near repeated lows.
              </p>
            </div>
          </div>

          <div>
            <span>3</span>
            <div>
              <strong>Volume helps confirm</strong>
              <p>
                Breakouts and bounces become more meaningful when
                supported by stronger volume.
              </p>
            </div>
          </div>
        </div>

        <div className="supportResistanceExampleBox">
          <span>PRACTICAL EXAMPLE</span>
          <p>
            If a stock keeps failing between <strong>$10.00</strong> and{" "}
            <strong>$10.05</strong>, that area can be marked as
            resistance. If the stock repeatedly bounces near{" "}
            <strong>$9.40</strong> to <strong>$9.45</strong>, that area
            can be marked as support.
          </p>
        </div>
      </section>

      <div className="lessonVisualExplanation">
        <div>
          <span>1</span>
          <p>Repeated reactions make a price zone more visible.</p>
        </div>

        <div>
          <span>2</span>
          <p>A breakout requires more than one brief move.</p>
        </div>

        <div>
          <span>3</span>
          <p>Old resistance may become new support.</p>
        </div>
      </div>
    </div>
  );
}

export default SupportResistanceVisual;